import type { AxiosResponse } from 'axios'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import type {
    CertificateApprovalDetail,
    CertificateApprovalPaper,
    CertificateApprovalPaperCertificate,
    CertificateApprovalsListResponse,
    CreateCertificateApprovalPayload,
    UpdateCertificateApprovalPayload
} from '@/api/modules/certificateApprovalsQueries'
import { certificateApprovalsService } from '@/views/Quality/services/certificateApprovalsService'

function isFscOrPefcCertificate(certificate: CertificateApprovalPaperCertificate): boolean {
    const label = `${certificate.name} ${certificate.code}`.toUpperCase()

    return label.includes('FSC') || label.includes('PEFC')
}

export const useQualityCertificateApprovalsStore = defineStore('qualityCertificateApprovals', () => {
    const currentApproval = ref<CertificateApprovalDetail | null>(null)
    const papers = ref<CertificateApprovalPaper[]>([])
    const paperCertificates = ref<CertificateApprovalPaperCertificate[]>([])

    async function fetchCertificateApprovals(queryString = ''): Promise<AxiosResponse<CertificateApprovalsListResponse>> {
        return certificateApprovalsService.fetchCertificateApprovals(queryString)
    }

    async function fetchCertificateApproval(approvalId: number | string): Promise<AxiosResponse<CertificateApprovalDetail>> {
        currentApproval.value = null
        const response = await certificateApprovalsService.fetchCertificateApproval(approvalId)
        currentApproval.value = response.data
        return response
    }

    async function updateCertificateApproval(
        approvalId: number | string,
        payload: UpdateCertificateApprovalPayload
    ): Promise<AxiosResponse<CertificateApprovalDetail>> {
        return certificateApprovalsService.updateCertificateApproval(approvalId, payload)
    }

    async function createCertificateApproval(
        payload: CreateCertificateApprovalPayload
    ): Promise<AxiosResponse<CertificateApprovalDetail>> {
        return certificateApprovalsService.createCertificateApproval(payload)
    }

    async function deleteCertificateApproval(approvalId: number | string): Promise<void> {
        return certificateApprovalsService.deleteCertificateApproval(approvalId)
    }

    function clearCurrentApproval(): void {
        currentApproval.value = null
    }

    async function fetchCertificateApprovalPapers(): Promise<void> {
        const response = await certificateApprovalsService.fetchCertificateApprovalPapers()
        papers.value = response.data.results.map(({ id, name }) => ({ id, name }))
    }

    async function fetchPaperCertificates(): Promise<void> {
        const response = await certificateApprovalsService.fetchPaperCertificates()
        paperCertificates.value = response.data.results.filter(isFscOrPefcCertificate)
    }

    return {
        currentApproval,
        papers,
        paperCertificates,
        fetchCertificateApprovals,
        fetchCertificateApproval,
        updateCertificateApproval,
        createCertificateApproval,
        deleteCertificateApproval,
        clearCurrentApproval,
        fetchCertificateApprovalPapers,
        fetchPaperCertificates
    }
})
